import { z } from "zod";
import { AgentHoursSchema } from "./hours";

const MenuModifierOptionSchema: z.ZodType<unknown> = z.object({
  id: z.string(),
  name: z.string(),
  price: z.number(),
  hidden: z.boolean().optional(),
  soldOut: z.boolean().optional(),
  stackable: z.boolean().optional(),
  isDefault: z.boolean().optional(),
  modifiers: z.array(z.lazy(() => MenuModifierSchema)).optional(),
});

const MenuModifierSchema: z.ZodType<unknown> = z.object({
  id: z.string(),
  name: z.string(),
  min: z.number(),
  max: z.number(),
  options: z.array(MenuModifierOptionSchema),
  hidden: z.boolean().optional(),
  forceRequired: z.boolean().optional(),
  freeModifierCount: z.number().optional(),
});

const MenuItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  price: z.number(),
  hidden: z.boolean().optional(),
  soldOut: z.boolean().optional(),
  description: z.string().optional(),
  taxes: z.array(z.string()).optional(),
  modifiers: z.array(MenuModifierSchema).optional(),
  availabilities: AgentHoursSchema.optional(),
  notes: z.string().optional(),
  prepTime: z.number().optional(),
});

const MenuCategorySchema = z.object({
  id: z.string(),
  name: z.string(),
  items: z.array(MenuItemSchema),
  description: z.string().optional(),
  hidden: z.boolean().optional(),
  availabilities: AgentHoursSchema.optional(),
});

const MenuSchema = z.array(MenuCategorySchema);

// Original nested schema (kept for backward compatibility)
export const MenuModuleConfigSchema = z.object({
  enabled: z.boolean().default(false),
  locked: z.boolean().default(false).optional(),

  categoryBlacklist: z.record(z.string(), z.boolean()).optional(),
  categoryNameOverrides: z.record(z.string(), z.string()).optional(),
  categoryDescriptionOverrides: z.record(z.string(), z.string()).optional(),
  categoryAvailabilityOverrides: z
    .record(z.string(), AgentHoursSchema)
    .optional(),
  categoryPrepTimeOverrides: z
    .record(z.string(), z.number().min(10))
    .optional(),
  itemBlacklist: z.record(z.string(), z.boolean()).optional(),
  itemNameOverrides: z.record(z.string(), z.string()).optional(),
  itemDescriptionOverrides: z.record(z.string(), z.string()).optional(),
  itemAvailabilityOverrides: z.record(z.string(), AgentHoursSchema).optional(),
  defaultOptionsOverrides: z
    .record(z.string(), z.record(z.string(), z.boolean()))
    .optional(),
  itemPrepTimeOverrides: z.record(z.string(), z.number().min(10)).optional(),
  notesMap: z.record(z.string(), z.string()).optional(),

  modifierBlacklist: z.record(z.string(), z.boolean()).optional(),
  modifierNameOverrides: z.record(z.string(), z.string()).optional(),
  forceRequired: z.record(z.string(), z.boolean()).optional(),
  freeModifierCount: z.record(z.string(), z.number().min(0)).optional(),
  allowStackedOverrides: z.record(z.string(), z.boolean()).optional(),

  optionBlacklist: z.record(z.string(), z.boolean()).optional(),
  optionNameOverrides: z.record(z.string(), z.string()).optional(),

  manualMenu: MenuSchema.optional(),
  replaceMap: z.array(z.tuple([z.string(), z.string()])).optional(),
});

export const MenuConfigFormSchema = z.object({
  menuEnabled: z.boolean().default(false),
  menuLocked: z.boolean().default(false),
  manualMenu: MenuSchema.optional(),
  menuReplaceMap: z.array(z.tuple([z.string(), z.string()])).optional(),
});

export type MenuConfigFormFieldPath =
  | `categoryBlacklist.${string}`
  | `categoryNameOverrides.${string}`
  | `categoryDescriptionOverrides.${string}`
  | `categoryAvailabilityOverrides.${string}`
  | `categoryPrepTimeOverrides.${string}`
  | `itemBlacklist.${string}`
  | `itemNameOverrides.${string}`
  | `itemDescriptionOverrides.${string}`
  | `itemAvailabilityOverrides.${string}`
  | `itemPrepTimeOverrides.${string}`
  | `defaultOptionsOverrides.${string}.${string}`
  | `notesMap.${string}`
  | `modifierBlacklist.${string}`
  | `modifierNameOverrides.${string}`
  | `forceRequired.${string}`
  | `freeModifierCount.${string}`
  | `allowStackedOverrides.${string}`
  | `optionBlacklist.${string}`
  | `optionNameOverrides.${string}`;

// Utility function to create flattened menu schema with dynamic fields
export const createMenuConfigFormSchema = (
  categories: string[],
  items: string[],
  modifiers: string[],
  options: string[],
) => {
  const dynamicFields: Record<string, z.ZodTypeAny> = {
    menuEnabled: z.boolean().default(false),
    menuLocked: z.boolean().default(false),
    menuId: z.string().optional(),
  };

  // Add category-based fields
  categories.forEach((categoryId) => {
    dynamicFields[`categoryBlacklist.${categoryId}`] = z.boolean().optional();
    dynamicFields[`categoryNameOverrides.${categoryId}`] = z
      .string()
      .optional();
    dynamicFields[`categoryDescriptionOverrides.${categoryId}`] = z
      .string()
      .optional();
    dynamicFields[`categoryAvailabilityOverrides.${categoryId}`] =
      AgentHoursSchema.optional();
    dynamicFields[`categoryPrepTimeOverrides.${categoryId}`] = z
      .number()
      .min(10)
      .optional();
  });

  // Add item-based fields
  items.forEach((itemId) => {
    dynamicFields[`itemBlacklist.${itemId}`] = z.boolean().optional();
    dynamicFields[`itemNameOverrides.${itemId}`] = z.string().optional();
    dynamicFields[`itemDescriptionOverrides.${itemId}`] = z.string().optional();
    dynamicFields[`itemAvailabilityOverrides.${itemId}`] =
      AgentHoursSchema.optional();
    dynamicFields[`itemPrepTimeOverrides.${itemId}`] = z
      .number()
      .min(10)
      .optional();
    dynamicFields[`notesMap.${itemId}`] = z.string().optional();
  });

  // Add modifier-based fields
  modifiers.forEach((modifierId) => {
    dynamicFields[`modifierBlacklist.${modifierId}`] = z.boolean().optional();
    dynamicFields[`modifierNameOverrides.${modifierId}`] = z
      .string()
      .optional();
    dynamicFields[`forceRequired.${modifierId}`] = z.boolean().optional();
    dynamicFields[`freeModifierCount.${modifierId}`] = z
      .number()
      .min(0)
      .optional();
    dynamicFields[`allowStackedOverrides.${modifierId}`] = z
      .boolean()
      .optional();
  });

  // Add option-based fields
  options.forEach((optionId) => {
    dynamicFields[`optionBlacklist.${optionId}`] = z.boolean().optional();
    dynamicFields[`optionNameOverrides.${optionId}`] = z.string().optional();
  });

  // Add default options overrides (item -> option mapping)
  items.forEach((itemId) => {
    options.forEach((optionId) => {
      dynamicFields[`defaultOptionsOverrides.${itemId}.${optionId}`] = z
        .boolean()
        .optional();
    });
  });

  // Add static fields
  dynamicFields.manualMenu = MenuSchema.optional();
  dynamicFields.menuReplaceMap = z
    .array(z.tuple([z.string(), z.string()]))
    .optional();

  return z.object(dynamicFields);
};
